import { Flame } from 'lucide-react';
import type { JSX } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useStudentActivity } from '@/features/memorization/hooks';
import type { ActivityDayView } from '@/features/memorization/types';
import { cn } from '@/lib/utils';

/**
 * "Ritme Setoran" (ADR-034) - how often this santri actually recorded
 * setoran, day by day, over the last month.
 *
 * It reports and does not judge. There is no quota here, no goal line, and
 * a quiet day is drawn as an empty square rather than a red mark:
 * memorization is worship before it is a metric, and a product that turns
 * a missed day into a reprimand earns attendance rather than sincerity
 * (ADR-034's own "what this does NOT do").
 *
 * Every number comes from the endpoint. Nothing is recomputed here - in
 * particular the streak, whose day boundaries depend on the reader's
 * timezone and are resolved server-side from the offset the request sent.
 */

const WINDOW_DAYS = 30;

/**
 * Four steps, not a continuous scale: with counts this small a gradient
 * would imply precision the data does not carry. The thresholds are
 * "none / one / a few / many", which is all a reader needs at a glance.
 */
function intensityClass(count: number): string {
  if (count === 0) {
    return 'bg-secondary';
  }
  if (count === 1) {
    return 'bg-primary/30';
  }
  if (count <= 3) {
    return 'bg-primary/60';
  }
  return 'bg-primary';
}

function formatDay(date: string): string {
  return new Date(`${date}T00:00:00.000Z`).toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

function dayLabel(day: ActivityDayView): string {
  if (day.submissionCount === 0) {
    return `${formatDay(day.date)}: tidak ada setoran`;
  }
  return `${formatDay(day.date)}: ${String(day.submissionCount)} setoran`;
}

function streakText(days: number, reachesWindowStart: boolean): string {
  if (days === 0) {
    return 'Belum ada rentetan';
  }
  // A streak that runs off the edge of the window is at least this long -
  // saying "30 hari" flat would report a capped number as though it were
  // the real one.
  return `${String(days)}${reachesWindowStart ? '+' : ''} hari berturut-turut`;
}

export function ActivityTrendCard({ studentId }: { readonly studentId: string }): JSX.Element | null {
  const { data, isLoading, isError } = useStudentActivity(studentId, WINDOW_DAYS);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ritme Setoran</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ritme Setoran</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertDescription>Gagal memuat ritme setoran.</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (data === undefined) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Ritme Setoran</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <span className="flex items-center gap-2">
            <Flame
              className={cn(
                'size-5',
                data.currentStreakDays > 0 ? 'text-primary' : 'text-muted-foreground',
              )}
              aria-hidden="true"
            />
            <span className="text-sm font-medium">
              {streakText(data.currentStreakDays, data.streakReachesWindowStart)}
            </span>
          </span>
          <span className="text-sm text-muted-foreground">
            {data.activeDays} hari aktif · {data.totalSubmissions} setoran dalam {WINDOW_DAYS} hari
            terakhir
          </span>
        </div>

        {/* A plain list semantically; the squares are the presentation. Each
            cell carries its own readable label, so the trend is available
            to a screen reader and not only to the eye. */}
        <ul className="flex flex-wrap gap-1" aria-label="Setoran per hari, 30 hari terakhir">
          {data.days.map((day) => (
            <li
              key={day.date}
              title={dayLabel(day)}
              aria-label={dayLabel(day)}
              className={cn('size-5 rounded-sm', intensityClass(day.submissionCount))}
            />
          ))}
        </ul>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span>Lebih sepi</span>
          <span className="flex items-center gap-1">
            <span className="size-3 rounded-sm bg-secondary" aria-hidden="true" />
            <span className="size-3 rounded-sm bg-primary/30" aria-hidden="true" />
            <span className="size-3 rounded-sm bg-primary/60" aria-hidden="true" />
            <span className="size-3 rounded-sm bg-primary" aria-hidden="true" />
          </span>
          <span>Lebih padat</span>
        </div>

        <p className="text-xs leading-relaxed text-muted-foreground">
          Menghitung hari yang ada setoran tercatat - bukan menilai kualitas hafalan, dan bukan
          target yang harus dikejar. Hari yang kosong tetap kosong, tanpa penalti.
        </p>
      </CardContent>
    </Card>
  );
}
